import React, { useState } from 'react';
import { Grid, Box } from '@mui/material';
import { MimshakData } from '../../interfaces/DTO/mimshakData';
import ScatterFundAmountChart from '../PersonFinancialData/Charts/ScatterFundAmountChart';
import PathDragExposureSummary from './PathDragExposureSummary';
import ExposureChangesTable from './ExposureChangesTable';
import ExposureAfterChangeSummary from './ExposureAfterChangeSummary';
import { fundService } from '../../services/fundService';
import { NewExposureMap } from '../../hooks/feeLab/usePathDragChanges';
import { PreferenceDistributionData } from '../../interfaces/DTO/contactPreferences';
import { FundSumDropMap } from '../../interfaces/DTO/simulation';
import { ChangesDataUtils } from './Reports/ChangesDataUtils';

interface ExposureLabDnDProps {
  modifiedRows: MimshakData[];
  originalRows: MimshakData[];
  isModified?: boolean;
  onPathDrag: (dragData: {
    policy_number: string;
    product_type: number | null;
    path_code: number;
    old_x_value: number;
    new_x_value: number;
    old_y_value: number;
    new_y_value: number;
    x_field: 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year';
    y_field: 'yield' | 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year' | 'standard_deviation';
  }) => void;
  dragChanges: Array<{
    policy_number: string;
    path_code: number;
    new_x_value: number;
    new_y_value: number;
    x_field: string;
    y_field: string;
  }>;
  newExposureMap: NewExposureMap;
  onRemovePolicy: (policyNumber: string) => void;
  onOpenPathDrawer?: (policyNumber: string) => void;
  preferenceData: PreferenceDistributionData;
  onPreferenceChange: (newData: PreferenceDistributionData) => void;
  fundSumDropMap?: FundSumDropMap;
}

const ExposureLabDnD: React.FC<ExposureLabDnDProps> = ({
  modifiedRows,
  originalRows,
  isModified = false,
  onPathDrag,
  dragChanges,
  newExposureMap,
  onRemovePolicy,
  onOpenPathDrawer,
  preferenceData,
  onPreferenceChange,
  fundSumDropMap,
}) => {
  const [scatterYAxisMode, setScatterYAxisMode] = useState<'yield' | 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year' | 'standard_deviation'>('stock_market_percent');
  const [scatterDragEnabled, setScatterDragEnabled] = useState<boolean>(true);
  const [xAxisField, setXAxisField] = useState<'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year'>('liquidity_year');
  // "after" view: show modified products in scatter chart, hide ExposureChangesTable
  const [showAfterView, setShowAfterView] = useState<boolean>(false);
  // Separate axis state for the "after" view chart
  const [afterYAxisMode, setAfterYAxisMode] = useState<'yield' | 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year' | 'standard_deviation'>('stock_market_percent');
  const [afterXAxisField, setAfterXAxisField] = useState<'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year'>('liquidity_year');

  const handleMetricClick = (metric: 'stock_market_percent' | 'foreign_currency_exposure_percent' | 'foreign_exposure_percent') => {
    setScatterYAxisMode(metric);
    if (metric === 'foreign_exposure_percent' || metric === 'foreign_currency_exposure_percent') {
      setXAxisField('stock_market_percent');
    } else {
      setXAxisField('liquidity_year');
    }
    if (!scatterDragEnabled) {
      setScatterDragEnabled(true);
    }
  };

  // Products with accumulated_amount adjusted by fundSumDropMap (extra spread proportionally across paths)
  const modifiedProductsWithEffectiveAmounts = React.useMemo(() => {
    return modifiedRows.flatMap(row => {
      const extra = ChangesDataUtils.getTotalFundAddition(fundSumDropMap?.[row.policy_number]);
      if (extra === 0) return row.data?.products || [];
      return (row.data?.products || []).map(product => ({
        ...product,
        policies_or_accounts: (product.policies_or_accounts || []).map(policy => {
          const paths = policy.investment_paths || [];
          const totalAccumulated = paths.reduce((sum, p) => sum + (p.accumulated_amount ?? 0), 0);
          const extraPerPath = paths.length > 0 ? extra / paths.length : 0;
          return {
            ...policy,
            investment_paths: paths.map(path => ({
              ...path,
              accumulated_amount: (path.accumulated_amount ?? 0) + (totalAccumulated > 0 ? extraPerPath : 0),
            })),
          };
        }),
      }));
    });
  }, [modifiedRows, fundSumDropMap]);

  // Get relevant fund data for scatter chart
  const allFundData = React.useMemo(() => {
    const fundIds = new Set<number>();
    originalRows.forEach(row => {
      if (row.fund_ids) row.fund_ids.forEach(id => fundIds.add(id));
    });
    modifiedRows.forEach(row => {
      if (row.fund_ids) row.fund_ids.forEach(id => fundIds.add(id));
    });
    return Array.from(fundIds)
      .map(id => fundService.getFundFromCacheById(id))
      .filter(fund => fund !== null);
  }, [originalRows, modifiedRows]);

  // Weighted exposures applying newExposureMap drag overrides
  const weightedExposures = React.useMemo(() => {
    const allPaths: any[] = [];
    modifiedRows.forEach(row => {
      const policies = row.data?.products?.flatMap(p => p.policies_or_accounts) || [];
      policies.forEach(policy => {
        const paths = policy.investment_paths || [];
        paths.forEach(path => {
          if (path.accumulated_amount) {
            allPaths.push({
              path_code: path.path_code,
              amount: path.accumulated_amount,
              policy_number: policy.policy_number,
            });
          }
        });
      });
    });

    let totalAccumulatedAmount = 0;
    let weightedStock = 0;
    let weightedCurrency = 0;
    let weightedForeign = 0;

    allPaths.forEach(path => {
      const fund = fundService.getFundFromCacheById(path.path_code);
      if (fund && fund.total_assets && path.amount) {
        const amount = path.amount;
        totalAccumulatedAmount += amount;

        let stockPercent = fund.stock_market_exposure ? (fund.stock_market_exposure / fund.total_assets) : 0;
        let currencyPercent = fund.foreign_currency_exposure ? (fund.foreign_currency_exposure / fund.total_assets) : 0;
        let foreignPercent = fund.foreign_exposure ? (fund.foreign_exposure / fund.total_assets) : 0;

        const policyChanges = newExposureMap[path.policy_number];
        if (policyChanges) {
          if (policyChanges.stock_market_percent !== undefined) stockPercent = policyChanges.stock_market_percent.new_value / 100;
          if (policyChanges.foreign_currency_exposure_percent !== undefined) currencyPercent = policyChanges.foreign_currency_exposure_percent.new_value / 100;
          if (policyChanges.foreign_exposure_percent !== undefined) foreignPercent = policyChanges.foreign_exposure_percent.new_value / 100;
        }

        weightedStock += stockPercent * amount;
        weightedCurrency += currencyPercent * amount;
        weightedForeign += foreignPercent * amount;
      }
    });

    return {
      stockExposure: totalAccumulatedAmount ? weightedStock / totalAccumulatedAmount : 0,
      currencyExposure: totalAccumulatedAmount ? weightedCurrency / totalAccumulatedAmount : 0,
      foreignExposure: totalAccumulatedAmount ? weightedForeign / totalAccumulatedAmount : 0,
    };
  }, [modifiedRows, newExposureMap]);

  // Original exposures (no drag overrides) — used for slider triangle position
  const originalWeightedExposures = React.useMemo(() => {
    const allPaths: any[] = [];
    originalRows.forEach(row => {
      const policies = row.data?.products?.flatMap(p => p.policies_or_accounts) || [];
      policies.forEach(policy => {
        (policy.investment_paths || []).forEach(path => {
          if (path.accumulated_amount) {
            allPaths.push({ path_code: path.path_code, amount: path.accumulated_amount });
          }
        });
      });
    });
    let total = 0, stock = 0, currency = 0, foreign = 0;
    allPaths.forEach(path => {
      const fund = fundService.getFundFromCacheById(path.path_code);
      if (fund && fund.total_assets && path.amount) {
        total += path.amount;
        stock += (fund.stock_market_exposure ? fund.stock_market_exposure / fund.total_assets : 0) * path.amount;
        currency += (fund.foreign_currency_exposure ? fund.foreign_currency_exposure / fund.total_assets : 0) * path.amount;
        foreign += (fund.foreign_exposure ? fund.foreign_exposure / fund.total_assets : 0) * path.amount;
      }
    });
    return {
      stockExposure: total ? stock / total : 0,
      currencyExposure: total ? currency / total : 0,
      foreignExposure: total ? foreign / total : 0,
    };
  }, [originalRows]);

  const handleAfterMetricClick = (metric: 'stock_market_percent' | 'foreign_currency_exposure_percent' | 'foreign_exposure_percent') => {
    setAfterYAxisMode(metric);
    if (metric === 'foreign_exposure_percent' || metric === 'foreign_currency_exposure_percent') {
      setAfterXAxisField('stock_market_percent');
    } else {
      setAfterXAxisField('liquidity_year');
    }
    if (!showAfterView) setShowAfterView(true);
  };

  const hasStockChanges = Object.values(newExposureMap).some(c => c.stock_market_percent !== undefined);
  const hasCurrencyChanges = Object.values(newExposureMap).some(c => c.foreign_currency_exposure_percent !== undefined);
  const hasForeignChanges = Object.values(newExposureMap).some(c => c.foreign_exposure_percent !== undefined);

  // When a simulation is loaded, dragChanges is empty but newExposureMap has data.
  // Reconstruct drag entries from the map so the scatter chart reflects saved positions.
  const effectiveDragChanges = React.useMemo(() => {
    if (dragChanges.length > 0) return dragChanges;
    const result: typeof dragChanges = [];
    Object.entries(newExposureMap).forEach(([policy_number, changes]) => {
      (
        [
          ['stock_market_percent', changes.stock_market_percent],
          ['foreign_currency_exposure_percent', changes.foreign_currency_exposure_percent],
          ['foreign_exposure_percent', changes.foreign_exposure_percent],
        ] as const
      ).forEach(([key, change]) => {
        if (change) {
          result.push({
            policy_number,
            path_code: change.path_code,
            new_x_value: change.new_value,
            new_y_value: change.new_value,
            x_field: key,
            y_field: key,
          });
        }
      });
    });
    return result;
  }, [dragChanges, newExposureMap]);

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: '0 0 8px 8px', p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          {showAfterView ? (
            <Box sx={{ backgroundColor: '#fffbe6', borderRadius: '8px', p: 1 }}>
              <ScatterFundAmountChart
                products={modifiedProductsWithEffectiveAmounts}
                fundData={allFundData}
                initialYieldField="cumulative_12m_yield"
                yAxisRange={undefined}
                initialColorMode="default"
                initialSelectedManagingCorporations={[]}
                autoSelectHighlighted={false}
                title="תרשים פיזור - לאחר שינוי"
                initialXAxisField="liquidity_year"
                initialYAxisMode="stock_market_percent"
                initialDragEnabled={false}
                xAxisField={afterXAxisField}
                onXAxisFieldChange={(field) => setAfterXAxisField(field)}
                yAxisMode={afterYAxisMode}
                onYAxisModeChange={setAfterYAxisMode}
                dragEnabled={false}
                preferenceData={preferenceData}
                onPreferenceChange={onPreferenceChange}
              />
            </Box>
          ) : (
            <>
              <ScatterFundAmountChart
                products={originalRows.flatMap(row => row.data?.products || [])}
                fundData={allFundData}
                initialYieldField="cumulative_12m_yield"
                yAxisRange={undefined}
                initialColorMode="default"
                initialSelectedManagingCorporations={[]}
                autoSelectHighlighted={false}
                title="תרשים פיזור - נזילות מול חשיפה למניות"
                initialXAxisField="liquidity_year"
                initialYAxisMode="stock_market_percent"
                initialDragEnabled={true}
                xAxisField={xAxisField}
                onXAxisFieldChange={(field) => setXAxisField(field)}
                yAxisMode={scatterYAxisMode}
                onYAxisModeChange={setScatterYAxisMode}
                dragEnabled={scatterDragEnabled}
                onDragEnabledChange={setScatterDragEnabled}
                onPathDrag={onPathDrag}
                dragChanges={effectiveDragChanges}
                preferenceData={preferenceData}
                onPreferenceChange={onPreferenceChange}
              />
            </>
          )}
          <ExposureChangesTable
            newExposureMap={newExposureMap}
            modifiedRows={modifiedRows}
            originalRows={originalRows}
            onRemovePolicy={onRemovePolicy}
            onOpenPathDrawer={onOpenPathDrawer}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Box
            onClick={showAfterView ? () => setShowAfterView(false) : undefined}
            sx={{
              cursor: showAfterView ? 'pointer' : 'default',
              outline: isModified && !showAfterView ? '2px solid #1976d2' : 'none',
              borderRadius: '4px',
              transition: 'outline 0.2s',
            }}
          >
            <PathDragExposureSummary
              stockExposure={weightedExposures.stockExposure}
              currencyExposure={weightedExposures.currencyExposure}
              foreignExposure={weightedExposures.foreignExposure}
              originalStockExposure={originalWeightedExposures.stockExposure}
              originalCurrencyExposure={originalWeightedExposures.currencyExposure}
              originalForeignExposure={originalWeightedExposures.foreignExposure}
              hasStockChanges={hasStockChanges}
              hasCurrencyChanges={hasCurrencyChanges}
              hasForeignChanges={hasForeignChanges}
              onMetricClick={handleMetricClick}
              activeMetric={!showAfterView && (scatterYAxisMode === 'stock_market_percent' || scatterYAxisMode === 'foreign_currency_exposure_percent' || scatterYAxisMode === 'foreign_exposure_percent') ? scatterYAxisMode : null}
              isDragEnabled={scatterDragEnabled}
              preferenceData={preferenceData}
              onPreferenceChange={onPreferenceChange}
            />
          </Box>
          {isModified && (
            <Box sx={{ mt: 2 }}>
              <ExposureAfterChangeSummary
                data={modifiedRows}
                originalData={originalRows}
                fundSumDropMap={fundSumDropMap}
                preferenceData={preferenceData}
                isActive={showAfterView}
                onCardClick={() => setShowAfterView(prev => !prev)}
                onMetricClick={handleAfterMetricClick}
                activeMetric={showAfterView && (afterYAxisMode === 'stock_market_percent' || afterYAxisMode === 'foreign_currency_exposure_percent' || afterYAxisMode === 'foreign_exposure_percent') ? afterYAxisMode : null}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExposureLabDnD;
